import React from "react";
import { PageHeader, StatePill } from "../components/Ui";
import { Link } from "react-router-dom";
import { useAuth, hasPermission, isAdmin } from "../context/AuthContext";
import { labPageUrl } from "../lib/workflowLinks";
import DataTableBlock from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useBootstrapList } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { PURCHASE_COLUMNS } from "./purchaseColumns";
import AdminEntityActions from "../components/AdminEntityActions";
import { formatListDate } from "../lib/purchaseListMeta";

export default function PurchasesPage() {
  const { user } = useAuth();
  const adminUser = isAdmin(user);
  const list = useBootstrapList({
    path: "/purchases/bootstrap",
    columns: PURCHASE_COLUMNS,
    defaultSort: { id: "code", desc: true },
    wsTypes: ["purchase"],
  });

  useBootstrapWS(["purchase"], list.reload);

  const title = list.table.hasActiveFilters
    ? `${list.meta.count} matching transactions`
    : `${list.meta.count} transactions`;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="purchases-page">
      <PageHeader
        eyebrow="Transaction Lifecycle"
        title="Purchase Transactions"
        description="Every paddy inflow tracked through a strict state machine — from gate-in to payable voucher."
      />


      <DataTableBlock
        title={title}
        columns={PURCHASE_COLUMNS}
        table={list.table}
        loading={list.loading}
        totalCount={list.meta.count}
        testId="purchases-table"
        minWidth="880px"
        emptyMessage="No transactions yet."
        emptyFilteredMessage="No purchases match your filters."
        onClearFilters={() => list.setPage(1)}
        pagination={{
          ...list.pagination,
          pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
        }}
        exportButton={(
          <DataTableExportButton
            apiPath="/purchases"
            params={list.exportParams}
            columns={PURCHASE_COLUMNS}
            filename="purchases.xlsx"
            testId="purchases-export"
          />
        )}
        renderRow={(p) => (
          <tr key={p.id} className="border-t hover:bg-accent/60" data-testid={`purchase-row-${p.code}`}>
            <td className="px-3 py-2.5 text-xs whitespace-nowrap tabular">{formatListDate(p.created_at) || "—"}</td>
            <td className="px-3 py-2.5 text-xs font-medium uppercase">{p.vehicle_number || "—"}</td>
            <td className="px-3 py-2.5 font-mono text-xs">{p.code}</td>
            <td className="px-3 py-2.5 font-medium">{p.party_name}</td>
            <td className="px-3 py-2.5">{p.variety}</td>
            <td className="px-3 py-2.5 tabular">{p.bags_reported}</td>
            <td className="px-3 py-2.5 tabular font-medium">{p.net_weight_qtl ?? "—"}</td>
            <td className="px-3 py-2.5 tabular">₹{p.negotiated_rate}</td>
            <td className="px-3 py-2.5"><StatePill state={p.state} /></td>
            <td className="px-3 py-2.5 text-xs space-y-0.5">
              {hasPermission(user, "page.contracts") && p.purchase_contract_id ? (
                <Link to="/contracts" className="block link-brand">
                  PAC #{p.purchase_contract_id}
                </Link>
              ) : null}
              {hasPermission(user, "page.lab") && ["WEIGH_GROSS", "LAB_PENDING", "BARGAINING"].includes(p.state) && p.lab_required !== false ? (
                <Link to={labPageUrl("truck")} className="block text-muted-foreground hover:link-brand">
                  Lab
                </Link>
              ) : null}
              {hasPermission(user, "page.weighbridge") && p.state === "GATE_IN" ? (
                <Link to="/weighbridge/inward" className="block text-muted-foreground hover:link-brand">
                  Weigh
                </Link>
              ) : null}
            </td>
            <td className="px-3 py-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <Link to={`/purchases/${p.id}`} className="text-xs link-brand" data-testid={`open-${p.code}`}>Open →</Link>
                {adminUser && p.state !== "REJECTED" && (
                  <Link
                    to={`/purchases/${p.id}?editLinks=1`}
                    className="text-xs text-muted-foreground hover:link-brand"
                    data-testid={`edit-links-${p.code}`}
                  >
                    Edit link
                  </Link>
                )}
                <AdminEntityActions
                  entityType="purchase"
                  entityId={p.id}
                  entityLabel={p.code}
                  editTo={`/purchases/${p.id}`}
                  onDeleted={list.reload}
                />
              </div>
            </td>
          </tr>
        )}
      />
    </div>
  );
}
